import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";

/* ------------------------------------------------------------------ *
 * Shared plumbing for the two history screens (/work-history and
 * /customer-history).
 *
 * Both read the same page of work orders — POST /wo/getAllBy/ with
 * `getAll: true` — and differ only in which columns they draw, so the
 * query, the toolbar, the pager and the product chips live here once.
 *
 * Fixes carried over from the legacy pair (workHistory.js /
 * customersHistory.js), which were byte-for-byte the same file bar the
 * columns:
 *   - `byQuery` was sent but the effect never watched it, so typing in
 *     the search box did nothing until another filter changed
 *   - changing the type or the date kept you on page 7 of a 2-page result
 *   - a failed request left the spinner up forever (setListIsLoading was
 *     only cleared on success)
 *   - `sortBy` was state, was set from a map that returned "date" for
 *     both types, and was then ignored — the request always sorted by
 *     `timestamp`. Dropped; the request says what it does.
 * ------------------------------------------------------------------ */

export const DATE_FMT = "DD/MM/YYYY";
export const PAGE_LIMIT = 25;

/* The list is ordered by entry timestamp, newest first. The date filter
   still targets the operational date each type is planned around, which
   is the field the legacy pages filtered on too. */
const DATE_FIELD = { Funeral: "funeral_date", Retail: "delivery_date" };

/* `date` is the DD/MM/YYYY the API stores; <input type="date"> speaks
   YYYY-MM-DD. */
export const toInputDate = (date) =>
  date ? moment(date, DATE_FMT).format("YYYY-MM-DD") : "";
export const fromInputDate = (value) =>
  value ? moment(value, "YYYY-MM-DD").format(DATE_FMT) : "";

export function useHistoryQuery(initialType = "Funeral") {
  const [orderType, setOrderType] = useState(initialType);
  const [date, setDate] = useState(""); // DD/MM/YYYY, "" = every date
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState(""); // `search` after debounce
  const [page, setPage] = useState(1);

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  /* One request per pause in typing rather than one per keystroke. */
  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  /* Any filter change invalidates the page number — page 7 of the old
     result is rarely page 7 of the new one. Reset during render rather
     than in an effect: an effect would let one request go out on the old
     page number before the reset fired a second one. */
  const filterKey = `${orderType}|${date}|${query}`;
  const [lastKey, setLastKey] = useState(filterKey);
  if (lastKey !== filterKey) {
    setLastKey(filterKey);
    setPage(1);
  }

  const refresh = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    axios
      .post(BASE_SERVER_URL + "/wo/getAllBy/", {
        filter: {
          type: orderType,
          ...(date ? { [DATE_FIELD[orderType]]: date } : {}),
        },
        page,
        byQuery: query,
        limit: PAGE_LIMIT,
        sortBy: "timestamp",
        sortDirection: -1,
        getAll: true,
      })
      .then((doc) => {
        if (doc.data.status !== "success") {
          setLoadError(true);
          setLoading(false);
          return;
        }
        setOrders(doc.data.response || []);
        setTotal(Number(doc.data.responseLength) || 0);
        setLoading(false);
      })
      .catch(() => {
        // the legacy pages left `loading` true here, so a failed request
        // showed ten skeleton rows for ever
        setLoading(false);
        setLoadError(true);
      });
  }, [orderType, date, query, page]);

  useEffect(refresh, [refresh]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const listStart = (page - 1) * PAGE_LIMIT;

  return {
    orderType,
    setOrderType,
    date,
    setDate,
    search,
    setSearch,
    query,
    page,
    setPage,
    pageCount,
    listStart,
    orders,
    total,
    loading,
    loadError,
    refresh,
  };
}

/* ---------------- toolbar ---------------- */

export function HistoryToolbar({ q, searchPlaceholder, children }) {
  return (
    <div className="hx-bar">
      <div className="rf-segmented">
        {["Funeral", "Retail"].map((type) => (
          <button
            key={type}
            type="button"
            className={q.orderType === type ? "is-active" : ""}
            onClick={() => q.setOrderType(type)}
          >
            {type}
          </button>
        ))}
      </div>

      <label className={`hx-date${q.date ? " is-set" : ""}`}>
        <span className="hx-date-k">
          {q.orderType === "Funeral" ? "Funeral" : "Delivery"}
        </span>
        <input
          type="date"
          value={toInputDate(q.date)}
          onChange={(e) => q.setDate(fromInputDate(e.target.value))}
        />
        {/* only offered once a date is set — clearing means "every date",
            which is how both pages start */}
        <button
          type="button"
          className="hx-date-clear"
          onClick={() => q.setDate("")}
          title="Show every date"
          style={{ visibility: q.date ? "visible" : "hidden" }}
        >
          ×
        </button>
      </label>

      <label className="hx-search">
        <span className="hx-search-icon" />
        <input
          type="search"
          value={q.search}
          placeholder={searchPlaceholder}
          onChange={(e) => q.setSearch(e.target.value)}
        />
      </label>

      <span className="hx-count rf-num">
        {q.loading ? "…" : <><b>{q.total}</b> {q.total === 1 ? "order" : "orders"}</>}
      </span>

      <span className="rf-spacer" />
      {children}
    </div>
  );
}

/* ---------------- pager ---------------- */

/* 1 … 4 5 [6] 7 8 … 42 — a history can run to hundreds of pages, so the
   legacy "render every page number" strip isn't an option. */
function pageWindow(page, pageCount) {
  const out = [];
  const push = (v) => {
    if (out[out.length - 1] !== v) out.push(v);
  };
  push(1);
  for (let p = page - 2; p <= page + 2; p += 1) {
    if (p > 1 && p < pageCount) push(p);
  }
  if (pageCount > 1) push(pageCount);
  const withGaps = [];
  out.forEach((p, i) => {
    if (i > 0 && p - out[i - 1] > 1) withGaps.push("gap" + p);
    withGaps.push(p);
  });
  return withGaps;
}

export function HistoryFoot({ q }) {
  const { page, pageCount, total, listStart, orders } = q;
  const shownTo = listStart + orders.length;
  return (
    <div className="hx-foot">
      <span className="hx-foot-note">
        {total === 0
          ? "Nothing to show"
          : `Showing ${listStart + 1}–${shownTo} of ${total}`}
      </span>
      <span className="rf-spacer" />
      {pageCount > 1 && (
        <div className="hx-pager">
          <button
            type="button"
            onClick={() => q.setPage(page - 1)}
            disabled={page <= 1}
            title="Previous page"
          >
            ‹
          </button>
          {pageWindow(page, pageCount).map((p) =>
            typeof p === "string" ? (
              <span key={p} className="hx-gap">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={p === page ? "is-active" : ""}
                onClick={() => q.setPage(p)}
              >
                {p}
              </button>
            )
          )}
          <button
            type="button"
            onClick={() => q.setPage(page + 1)}
            disabled={page >= pageCount}
            title="Next page"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------- cells ---------------- */

/* Selections are stored either as an array or as a JSON string of one,
   depending on which version of the order form wrote them. */
export function parseSelection(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

const labelOf = (item) => {
  const isCover = (item.product_type || "").toLowerCase() === "casket cover";
  // a casket cover is known by its code on the bench, everything else by name
  const base =
    (isCover && item.code) ||
    item.product_name ||
    item.code ||
    item.product_type ||
    "Product";
  const size = item.size && String(item.size).toLowerCase() !== "none" ? item.size : "";
  return size ? `${base} · ${size}` : base;
};

const isCover = (item) =>
  ["casket cover", "singles/petals"].indexOf(
    (item.product_type || "").toLowerCase()
  ) !== -1;

/* One chip per line item. The legacy cell ran every item together in a
   single comma-separated string inside a 30%-wide column. */
export function ProductChips({ groups, empty = "No products" }) {
  const items = useMemo(
    () => groups.reduce((acc, raw) => acc.concat(parseSelection(raw)), []),
    [groups]
  );
  if (items.length === 0) return <span className="hx-empty-v">{empty}</span>;
  return (
    <span className="hx-chips">
      {items.map((item, i) => (
        <span
          key={i}
          className={`hx-chip${isCover(item) ? " is-cc" : ""}`}
          title={item.comment || undefined}
        >
          <b>{Number(item.amount) || 1}×</b>
          {labelOf(item)}
        </span>
      ))}
    </span>
  );
}

export function Text({ value, empty = "—" }) {
  return value ? (
    <>{value}</>
  ) : (
    <span className="hx-empty-v">{empty}</span>
  );
}

export function EntryDate({ value }) {
  if (!value) return <span className="hx-empty-v">—</span>;
  const m = moment(value);
  if (!m.isValid()) return <>{String(value)}</>;
  return (
    <span className="hx-date-v">
      {m.format("D MMM YYYY")}
      <span>{m.format("HH:mm")}</span>
    </span>
  );
}

/* The day heading rows. Orders arrive newest-first by `timestamp`, so a
   heading appears wherever the entry day changes. */
export function groupRows(orders) {
  const out = [];
  let current = null;
  orders.forEach((order, i) => {
    const day = moment(order.date || order.timestamp);
    const key = day.isValid() ? day.format("YYYY-MM-DD") : "unknown";
    if (key !== current) {
      current = key;
      out.push({
        group: key,
        label: day.isValid() ? day.format("dddd D MMMM YYYY") : "Undated",
        count: orders.filter((o) => {
          const d = moment(o.date || o.timestamp);
          return (d.isValid() ? d.format("YYYY-MM-DD") : "unknown") === key;
        }).length,
      });
    }
    out.push({ order, index: i });
  });
  return out;
}

export function SkeletonRows({ rows = 8, cols }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }).map((__, c) => (
            <td key={c}>
              <div
                className="hx-skel"
                style={{ width: c === 0 ? "60%" : c % 3 === 0 ? "55%" : "80%" }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/* Loading / error / empty, drawn inside the table so the header stays put. */
export function TableState({ q, cols, emptyLead, emptyHint }) {
  if (q.loading) return <SkeletonRows cols={cols} />;
  if (q.loadError) {
    return (
      <tr>
        <td colSpan={cols}>
          <div className="hx-state">
            <b>Couldn&apos;t load this list.</b>
            The server didn&apos;t answer that request.
            <div>
              <button type="button" className="rf-btn rf-btn-ghost" onClick={q.refresh}>
                Try again
              </button>
            </div>
          </div>
        </td>
      </tr>
    );
  }
  if (q.orders.length === 0) {
    return (
      <tr>
        <td colSpan={cols}>
          <div className="hx-state">
            <b>{emptyLead}</b>
            {emptyHint}
          </div>
        </td>
      </tr>
    );
  }
  return null;
}
